import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import { AutoRefreshCard } from '@/Domain/components/KnowledgeBase/AutoRefreshCard';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import { KnowledgeManagerLoader } from '@/UI/KnowledgeBase/KnowledgeManager/KnowledgeManager.loader';

const getProgressColor = (percent: number) => {
  if (percent >= 100) return 'bg-destructive';
  if (percent >= 90) return 'bg-warning';
  return 'bg-foreground';
};

const getTextColor = (percent: number) => {
  if (percent >= 100) return 'text-destructive';
  if (percent >= 90) return 'text-warning';
  return 'text-muted-foreground';
};

const HeaderUsage = () => {
  const { t } = useTranslation(['knowledge_base']);
  const { getConfig } = useSubscription();
  const articlesLimit = getConfig('articles_limit') as number | null | undefined;
  const articlesUsed = (getConfig('articles_used') as number | undefined) ?? 0;

  // Custom (Enterprise) tier has no limit; loading state also lands here.
  if (articlesLimit === null || articlesLimit === undefined) return null;

  const percentUsed = Math.min(100, (articlesUsed / articlesLimit) * 100);

  return (
    <div
      className="flex items-center gap-2"
      data-testid="kb-header-usage"
      role="group"
      aria-label={t('usage_header.label', {
        used: articlesUsed,
        limit: articlesLimit,
      })}
    >
      <span className="text-xs font-medium text-muted-foreground">{t('usage_header.title')}</span>
      <div className="h-1.5 w-32 overflow-hidden rounded-full bg-muted-foreground/15">
        <div
          className={classNames(
            'h-full rounded-full transition-all duration-500 ease-out',
            getProgressColor(percentUsed),
          )}
          style={{ width: `${percentUsed}%` }}
        />
      </div>
      <span className={classNames('text-xs font-medium tabular-nums', getTextColor(percentUsed))}>
        {t('usage_header.count', {
          used: articlesUsed.toLocaleString(),
          limit: articlesLimit.toLocaleString(),
        })}
      </span>
    </div>
  );
};

export const KnowledgeBasePage = () => {
  const { t } = useTranslation(['knowledge_base']);

  return (
    <div className="min-w-0 max-w-full overflow-x-hidden">
      <div className="mb-6 flex flex-col items-start gap-2 md:flex-row md:items-baseline md:gap-6">
        <h1 className="text-lg font-semibold text-foreground">{t('title')}</h1>
        <HeaderUsage />
      </div>
      <AutoRefreshCard />
      <KnowledgeManagerLoader />
      {/* Outlet renders ArticleSheet modal when visiting /articles/:id routes */}
      <Outlet />
    </div>
  );
};
